<script setup lang="ts">
import { onMounted } from 'vue'
import { definePage } from 'vue-router/auto'
import { usePage } from '~/services/utils/usePage'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '~/components/ui/card'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Input } from '~/components/ui/input'
import { Button } from '~/components/ui/button'
import { Alert, AlertDescription } from '~/components/ui/alert'
import { useFlowSettingsService } from '~/services/settings/useFlowSettingsService'
import { CheckCircle2, Loader2 } from 'lucide-vue-next'

// Hidden until EMR has GET/PUT /settings/flow-preferences (#4994); delete this to show the page.
definePage({ redirect: '/settings' })

const { setPageName } = usePage()

const { form, isLoading, isUpdated, focusOptions, locationOptions, saveSettings } =
  useFlowSettingsService()

onMounted(() => {
  setPageName('My Flow Settings')
})
</script>

<template>
  <div class="grid gap-5">
    <Card class="bg-muted/50">
      <CardHeader>
        <CardTitle>{{ $t('Scheduling Preferences') }}</CardTitle>
        <CardDescription>
          {{ $t('Configure how the AI scheduling assistant suggests appointments for you.') }}
        </CardDescription>
      </CardHeader>
      <CardContent class="space-y-6">
        <Alert v-if="isUpdated" class="border-green-500 bg-green-50">
          <CheckCircle2 class="h-4 w-4 text-green-600" />
          <AlertDescription class="text-green-600">
            {{ $t('Settings saved successfully') }}
          </AlertDescription>
        </Alert>

        <div class="space-y-2">
          <Label for="focus-preference">{{ $t('Focus Preference') }}</Label>
          <Select v-model="form.focus_preference">
            <SelectTrigger id="focus-preference">
              <SelectValue :placeholder="$t('Select preference')" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="option in focusOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </SelectItem>
            </SelectContent>
          </Select>
          <p class="text-sm text-muted-foreground">
            {{ $t('When do you prefer to have concentrated appointment blocks?') }}
          </p>
        </div>

        <div class="space-y-2">
          <Label for="location-anchor">{{ $t('Location Anchor') }}</Label>
          <Select v-model="form.location_anchor">
            <SelectTrigger id="location-anchor">
              <SelectValue :placeholder="$t('Select location strategy')" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="option in locationOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </SelectItem>
            </SelectContent>
          </Select>
          <p class="text-sm text-muted-foreground">
            {{ $t('Where should appointments be scheduled relative to your base location?') }}
          </p>
        </div>

        <div class="space-y-2">
          <Label for="max-gap">{{ $t('Max Daily Gap (minutes)') }}</Label>
          <Input
            id="max-gap"
            v-model.number="form.max_daily_gap"
            type="number"
            :min="0"
            :max="480"
          />
          <p class="text-sm text-muted-foreground">
            {{
              $t(
                'Maximum total gap time between appointments in a day. Lower values create tighter schedules.',
              )
            }}
          </p>
        </div>

        <div class="space-y-2">
          <Label for="travel-buffer">{{ $t('Travel Buffer (minutes)') }}</Label>
          <Input
            id="travel-buffer"
            v-model.number="form.hard_rules!.travel_buffer_minutes"
            type="number"
            :min="0"
            :max="120"
          />
          <p class="text-sm text-muted-foreground">
            {{ $t('Minimum time required between appointments at different locations.') }}
          </p>
        </div>
      </CardContent>
      <CardFooter class="border-t p-4">
        <Button :disabled="isLoading" @click="saveSettings">
          <Loader2 v-if="isLoading" class="mr-2 h-4 w-4 animate-spin" />
          {{ $t('Save Preferences') }}
        </Button>
      </CardFooter>
    </Card>
  </div>
</template>
